import { StableWebSocket } from 'infrastructure/utility/StableWebSocket';
import { VehicleStatus, parseVehicleStatus } from './api/Control/VehicleStatus';
import { DriveStatus, parseDriveStatus } from './api/Control/DriveStatus';
import { Vehicle } from 'core/models/vehicle';
import { Gear } from 'core/models/drive';
import { Project } from 'core/models/project';
import { createKeyedListenerManager } from 'utility/KeyedListenerManager';
import { VehicleStatusListener } from 'core/repositories/VehicleRepository';
import { updateVehicleStatus } from 'infrastructure/utility/VehicleStatusUtility';

type VehicleStatusMessage = {
    type: 'VehicleStatus';
    body: VehicleStatus;
}

type DriveStatusMessage = {
    type: 'DriveStatus';
    body: DriveStatus;
}

const VEHICLE_STATUS_UPDATE_INTERVAL = 100; // [ms]

export class VehicleStatusSubscriber {
    private readonly ws: StableWebSocket;
    private readonly onError: (error: unknown) => void;
    private latestMessageEvent: { vehicleId?: string; statusDelta: Partial<Vehicle.Status> } | null = null;

    constructor(ws: StableWebSocket, onError: (error: unknown) => void) {
        this.onError = onError;
        this.ws = ws;

        this.setupListeners(ws);

        setInterval(() => {
            this.tick();
        }, VEHICLE_STATUS_UPDATE_INTERVAL);
    }

    private readonly statusTable = new Map<Vehicle.Id, Vehicle.Status>();

    private tick() {
        const initialStatus: Vehicle.Status = {
            currentSpeed: 0,
            targetSpeed: 0,
            stateMessage: '',
            steering: 0,
            accel: 0,
            brake: 0,
            gear: Gear.P,
            blinker: null,
        };

        const msg = this.latestMessageEvent;
        if (!msg) return;
        this.latestMessageEvent = null;

        const { vehicleId, statusDelta } = msg;
        if (vehicleId === undefined) return;

        const newStatus = updateVehicleStatus(this.statusTable.get(vehicleId) ?? initialStatus, statusDelta);
        this.statusTable.set(vehicleId, newStatus);

        const listeners = this.statusListenerManager.getListeners(vehicleId) ?? [];
        [...listeners].forEach(listener => {
            listener(newStatus);
        });
    }

    private async setupListeners(ws: StableWebSocket) {
        ws.addEventListener('message', msg => {
            const status = this.parseStatusMessage(msg);
            if (status) {
                this.latestMessageEvent = status;
            }
        });

        ws.addEventListener('error', err => {
            console.error('ws for CT status got an error', err);
        });

        ws.addEventListener('close', () => {
            console.warn('ws for CT status is closed');
            const knownProjects = new Set(this.vehicleToProject.values());
            knownProjects.forEach(projectId => {
                this.updateSubscription(projectId);
            });
        });
    }

    private parseStatusMessage(msg: MessageEvent) {
        const json = JSON.parse(msg.data) as DriveStatusMessage | VehicleStatusMessage;

        switch (json.type) {
            case 'DriveStatus':
                return parseDriveStatus(json.body);
            case 'VehicleStatus':
                return parseVehicleStatus(json.body);
            default:
                return undefined;
        }
    }

    private readonly vehicleToProject = new Map<Vehicle.Id, Project.Id>();
    private readonly subscribingVehicles = new Map<Vehicle.Id, number>();

    private readonly statusListenerManager = createKeyedListenerManager<Vehicle.Id, VehicleStatusListener>(vehicleId => {
        const projectId = this.vehicleToProject.get(vehicleId);
        if (!projectId) {
            // something's very wrong
            return () => { /* nothing to do */ };
        }

        if (this.incrementSubscription(vehicleId) === 1) {
            this.updateSubscription(projectId);
        }

        return () => {
            if (this.decrementSubscription(vehicleId) === 0) {
                this.updateSubscription(projectId);
            }
        };
    });

    private incrementSubscription(vehicleId: Vehicle.Id) {
        const count = this.subscribingVehicles.get(vehicleId) ?? 0;
        this.subscribingVehicles.set(vehicleId, count + 1);
        return count + 1;
    }

    private decrementSubscription(vehicleId: Vehicle.Id) {
        const count = this.subscribingVehicles.get(vehicleId) ?? 0;

        if (count === 1) {
            this.subscribingVehicles.delete(vehicleId);
        } else {
            this.subscribingVehicles.set(vehicleId, count - 1);
        }
        return count - 1;
    }

    private async updateSubscription(projectId: Project.Id) {
        const vehicleIds = [...this.subscribingVehicles.keys()];
        try {
            this.ws.send(JSON.stringify({
                'type': 'SubscribeVehicleStatusRequest',
                'body': {
                    'project_id': projectId,
                    'vehicle_ids': vehicleIds
                }
            }));
        } catch (e) {
            this.onError(e);
        }
    }

    addVehicleStatusListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: VehicleStatusListener): void {
        this.vehicleToProject.set(vehicleId, projectId);
        this.statusListenerManager.addListener(vehicleId, listener);
    }

    removeVehicleStatusListener(vehicleId: Vehicle.Id, _projectId: Project.Id, listener: VehicleStatusListener): void {
        this.statusListenerManager.removeListener(vehicleId, listener);
        this.vehicleToProject.delete(vehicleId);
    }

    /*
    private findProjectId(vehicleId: Vehicle.Id) {
        return this.vehicleToProject.get(vehicleId);
    }
    */
}
